/**
 * Persists the user's most recently selected license across uploads.
 *
 * clip2commons defaults every new upload's license to CC BY-SA 4.0 (see
 * makeFreshForm in App.vue). Contributors uploading a batch of files
 * under the same non-default license (e.g. CC BY 4.0) otherwise have to
 * re-select it every time. Remembering the last preset choice in
 * localStorage and using it as the default removes that repetition
 * while preserving today's behavior for first-time / no-history users.
 *
 * Only preset licenses are remembered - "custom" and "unknown" choices
 * are per-file exceptions, not a preference worth carrying forward.
 */
import type { LicenseId } from "./types"

const STORAGE_KEY_LAST_LICENSE = "c2c_last_license"

const PRESET_LICENSE_IDS: readonly LicenseId[] = [
  "cc-by-sa-4.0", "cc-by-4.0", "cc-zero",
  "cc-by-sa-3.0", "cc-by-3.0",
  "cc-by-sa-2.5", "cc-by-2.5",
  "cc-by-sa-2.0", "cc-by-2.0",
  "gfdl", "fal", "pd-self",
]

function isValidLicenseId(value: unknown): value is LicenseId {
  return typeof value === "string" &&
    (PRESET_LICENSE_IDS as readonly string[]).includes(value)
}

export function getSavedLicense(fallback: LicenseId): LicenseId {
  try {
    const saved = localStorage.getItem(STORAGE_KEY_LAST_LICENSE)
    return isValidLicenseId(saved) ? saved : fallback
  } catch {
    return fallback
  }
}

export function saveLicense(id: LicenseId): void {
  try {
    localStorage.setItem(STORAGE_KEY_LAST_LICENSE, id)
  } catch {
  }
}
